export const MOBILITY_TESTS = [
  { key: 'slr',          label: 'SLR (Straight Leg Raise)' },
  { key: 'add_longus',   label: 'Adduction' },
  { key: 'add_brevis_45', label: 'Abduction' },
  { key: 'quadriceps',   label: 'Quadriceps' },
  { key: 'hip_endo',     label: 'Hip Endorotation' },
  { key: 'hip_exo',      label: 'Hip Exorotation' },
  { key: 'hip_ext',      label: 'Hip Extension' },
  { key: 'knee_to_wall', label: 'Knee to Wall' },
];

export const STRENGTH_TESTS = [
  { key: 'hip_flexie',   label: 'Hip Flexion' },
  { key: 'abduction_45', label: 'Abduction 45°' },
  { key: 'add_longus',   label: 'Adductor Longus' },
  { key: 'add_brevis_45', label: 'Adductor Brevis 45°' },
  { key: 'abductie',     label: 'Abduction' },
  { key: 'soleus',       label: 'Soleus' },
  { key: 'hamstring',    label: 'Hamstring' },
  { key: 'quadriceps',   label: 'Quadriceps' },
];

export const SKINFOLD_SITES = [
  'Triceps', 'Subscapular', 'Biceps', 'Iliac crest',
  'Supraspinale', 'Abdominal', 'Front thigh', 'Medial calf',
];

export const num = v => (v === '' || v === null || v === undefined ? null : Number(v));

export const avg2 = (a, b) => {
  const vals = [a, b].map(num).filter(v => v !== null && !isNaN(v));
  return vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;
};

// Capped LSI: always 0-100 regardless of which side is higher.
export function lsiPct(left, right) {
  const l = num(left), r = num(right);
  if (!l || !r) return null;
  const lower = Math.min(l, r);
  const higher = Math.max(l, r);
  return (lower / higher) * 100;
}
export function lsiColor(pct) {
  if (pct === null) return 'var(--text-muted)';
  if (pct >= 95) return '#10b981';
  if (pct >= 90) return '#f59e0b';
  return '#ef4444';
}

export function hqRatio(row) {
  const ham  = avg2(row.hamstring_left, row.hamstring_right);
  const quad = avg2(row.quadriceps_left, row.quadriceps_right);
  if (!ham || !quad) return null;
  return ham / quad;
}
export function hqColor(v) {
  if (v === null) return 'var(--text-muted)';
  if (v < 0.6) return '#ef4444';
  if (v < 0.8) return '#f59e0b';
  return '#10b981';
}

export function abAdRatio(row) {
  const ab = avg2(row.abductie_left, row.abductie_right);
  const addLongus = avg2(row.add_longus_left, row.add_longus_right);
  if (ab === null || addLongus === null || !addLongus) return null;
  return (ab / addLongus) * 100;
}
export function abAdColor(v) {
  if (v === null) return 'var(--text-muted)';
  const diff = Math.abs(v - 100);
  if (diff > 20) return '#ef4444';
  if (diff > 10) return '#f59e0b';
  return '#10b981';
}

export function pctDiff(m1, m2) {
  const a = num(m1), b = num(m2);
  if (a === null || b === null || !a) return null;
  return Math.abs(a - b) / a * 100;
}
export function median(vals) {
  const s = [...vals].sort((a, b) => a - b);
  const mid = Math.floor(s.length / 2);
  return s.length % 2 ? s[mid] : (s[mid - 1] + s[mid]) / 2;
}

// percentile = (count of players strictly below you) / (total - 1) * 100.
// Best scorer -> 100%, worst -> 0%, mid-pack -> ~50%.
export function calculateISRTPercentile(playerStage, allPlayersLatestStages) {
  const total = allPlayersLatestStages.length;
  if (total <= 1) return null;
  const lowerCount = allPlayersLatestStages.filter(s => s < playerStage).length;
  return Math.round((lowerCount / (total - 1)) * 100);
}

export function getISRTColor(pct) {
  if (pct === null) return '#888780';
  if (pct < 25) return '#E24B4A';
  if (pct < 50) return '#EF9F27';
  if (pct < 75) return '#A8C24A';
  return '#639922';
}

// Same 0-100 "goodness" color scale as ISRT %, reused for any other percentile-of-squad
// metric (e.g. Sprint 10m/30m %) — the scale only cares about the percentile value,
// not which underlying test it came from.
export const getPercentileColor = getISRTColor;

// CMJ peak power is entered as relative power (W/kg); absolute Peak Power (W) is
// derived from that and the player's bodyweight rather than entered separately.
export function cmjPeakPowerWatts(wkg, weightKg) {
  const w = num(wkg), kg = num(weightKg);
  if (w === null || !kg) return null;
  return w * kg;
}

// ── Squad Profiling (quadrant matrix) ─────────────────────────────────────────
// Width of the "uncertain zone" band either side of each axis' mean, expressed as
// a multiple of that axis' standard deviation. Centralised here so it can be
// swapped for a typical_error lookup later without touching the chart component.
export const UNCERTAIN_ZONE_SD = 0.25;

export function mean(values) {
  if (!values.length) return null;
  return values.reduce((s, v) => s + v, 0) / values.length;
}

export function stdDev(values) {
  if (values.length < 2) return null;
  const m = mean(values);
  const variance = values.reduce((s, v) => s + (v - m) ** 2, 0) / values.length;
  return Math.sqrt(variance);
}

// Quadrant I = top-right (high X, high Y) ... IV = bottom-right (high X, low Y),
// numbered clockwise from top-right per standard quadrant convention.
export function getQuadrant(x, y, meanX, meanY) {
  if (x >= meanX && y >= meanY) return 1;
  if (x < meanX && y >= meanY) return 2;
  if (x < meanX && y < meanY) return 3;
  return 4;
}

// Short descriptive label — used on chart corners, sidebar headings, and tooltip.
export const QUADRANT_LABELS = {
  1: 'Complete explosive athlete',
  2: 'Powerful but less reactive',
  3: 'Room to grow',
  4: 'Reactive but less powerful',
};

// Full staff sidebar descriptions — analytical detail for coaches/physios.
export const QUADRANT_STAFF_DESCRIPTIONS = {
  1: 'High reactive strength and high peak power. Strong in both single explosive efforts and repeated sprint actions. Physically well-rounded from a power profile perspective.',
  2: 'High maximal power output but lower reactive strength. Generates large forces in single efforts but may be less efficient in repeated rapid-contact actions like sprinting. Potential to develop tendon stiffness and stretch-shortening cycle efficiency.',
  3: 'Below squad mean on both metrics. Clear physical development potential in explosive capacity. This is relative to the current squad — use alongside other test data and context before drawing conclusions.',
  4: 'Good reactive strength and tendon efficiency but relatively lower peak power output. Efficient in repeated fast movements but may lack maximal force in single explosive efforts. Potential to develop maximal strength base.',
};

// Player-facing captions — plain language, non-alarming, personalised tone.
export const QUADRANT_CAPTIONS = {
  1: "You score well on both power and reactive strength — you're physically explosive in both single efforts and repeated movements.",
  2: "You have strong explosive power. There's room to improve the efficiency of your repeated explosive movements — like sprint steps.",
  3: "Compared to the squad average, there's room to develop in both power and reactive strength. This is one data point — your coach can tell you more.",
  4: "You're efficient in quick, repeated explosive movements. There's room to grow in maximal power — for example through strength work.",
};

// Short explanations shown in the chart legend underneath the matrix.
export const QUADRANT_EXPLANATIONS = {
  1: 'High reactive strength AND high peak power — strong in both.',
  2: 'High peak power, lower reactive strength.',
  3: 'Below average on both metrics — development potential.',
  4: 'High reactive strength, lower peak power.',
};

// Returns 'both' | 'x' | 'y' | null depending on which axes the player sits within
// the uncertain band, enabling the sidebar to show a precise borderline note.
export function getUncertainType(x, y, meanX, meanY, bandX, bandY) {
  const inX = bandX != null && Math.abs(x - meanX) <= bandX;
  const inY = bandY != null && Math.abs(y - meanY) <= bandY;
  if (inX && inY) return 'both';
  if (inX) return 'x';
  if (inY) return 'y';
  return null;
}

// True if a player's point sits inside the uncertain band on BOTH axes — used for
// the desaturated chart marker (the chart's own visual signal for uncertainty).
export function isInUncertainZone(x, y, meanX, meanY, bandX, bandY) {
  return getUncertainType(x, y, meanX, meanY, bandX, bandY) === 'both';
}

// ── Quadrant matrix #2: Acceleration (10m Sprint) vs Endurance Capacity (ISRT Stage) ──
// Sprint time is "lower is better", the opposite direction from RSI/CMJ. QuadrantMatrix
// negates it before plotting (see SPRINT_ISRT_METRIC.xTransform) so faster players still
// land further right — the top-right quadrant stays the "best" one, same convention as
// the RSI/CMJ matrix.
export const SPRINT_ISRT_QUADRANT_LABELS = {
  1: 'Fast and high endurance capacity',
  2: 'High endurance capacity but lacks acceleration',
  3: 'Room to grow',
  4: 'Fast but lacks endurance capacity',
};

export const SPRINT_ISRT_QUADRANT_STAFF_DESCRIPTIONS = {
  1: 'High acceleration and high endurance capacity. Strong in both a single explosive sprint effort and sustained high-intensity output.',
  2: 'High endurance capacity but comparatively lower acceleration. Well-conditioned for repeated efforts; may benefit from acceleration-focused work.',
  3: 'Below squad mean on both metrics. Clear development potential in acceleration and endurance capacity. This is relative to the current squad — use alongside other test data and context before drawing conclusions.',
  4: 'High acceleration but comparatively lower endurance capacity. Good raw speed; may benefit from conditioning work to build endurance capacity.',
};

export const SPRINT_ISRT_QUADRANT_CAPTIONS = {
  1: "You score well on both acceleration and endurance capacity — you're quick off the mark and can sustain high-intensity efforts.",
  2: "You have strong endurance capacity. There's room to improve your acceleration.",
  3: "Compared to the squad average, there's room to develop in both acceleration and endurance capacity. This is one data point — your coach can tell you more.",
  4: "You have strong acceleration. There's room to grow in endurance capacity — for example through conditioning work.",
};

export const SPRINT_ISRT_QUADRANT_EXPLANATIONS = {
  1: 'High acceleration AND high endurance capacity — strong in both.',
  2: 'High endurance capacity, comparatively lower acceleration.',
  3: 'Below average on both metrics — development potential.',
  4: 'High acceleration, comparatively lower endurance capacity.',
};

// ── Squad Profiling metric configs ────────────────────────────────────────────
// Each config fully describes one quadrant matrix for QuadrantMatrix: which fields
// to read off a squad-profiling row, a self-inverse transform into "higher is
// better" plot space (identity for RSI/CMJ, negation for sprint time), and the
// copy shown on the chart/sidebar/tooltip/table.
export const RSI_CMJ_METRIC = {
  xKey: 'mean_rsi', yKey: 'peak_power_wkg',
  xTransform: v => v, yTransform: v => v,
  xLabel: 'Hop Test RSI', yLabel: 'CMJ Peak Power (W/kg)',
  xColumnLabel: 'Hop RSI', yColumnLabel: 'Peak Power (W/kg)',
  xFormat: v => v.toFixed(2), yFormat: v => v.toFixed(1),
  tooltipXName: 'RSI', tooltipXUnit: '', tooltipYName: 'Peak Power', tooltipYUnit: ' W/kg',
  legendText: 'RSI = how quickly you spring off the ground · Peak Power = how explosive your jump is',
  emptyStateText: 'No players have both Hop Test and CMJ results yet.',
  labels: QUADRANT_LABELS, descriptions: QUADRANT_STAFF_DESCRIPTIONS,
  captions: QUADRANT_CAPTIONS, explanations: QUADRANT_EXPLANATIONS,
};

export const SPRINT_ISRT_METRIC = {
  xKey: 'sprint_10m', yKey: 'final_stage',
  xTransform: v => -v, yTransform: v => v,
  xLabel: 'Acceleration (10m Sprint, s) — faster →', yLabel: 'Endurance Capacity (ISRT Stage)',
  xColumnLabel: 'Acceleration (s)', yColumnLabel: 'Endurance Capacity',
  xFormat: v => v.toFixed(2), yFormat: v => v.toFixed(0),
  tooltipXName: 'Acceleration', tooltipXUnit: 's', tooltipYName: 'Endurance Capacity', tooltipYUnit: '',
  legendText: 'Acceleration = 10m sprint time · Endurance Capacity = ISRT stage',
  emptyStateText: 'No players have both a 10m Sprint and ISRT result yet.',
  labels: SPRINT_ISRT_QUADRANT_LABELS, descriptions: SPRINT_ISRT_QUADRANT_STAFF_DESCRIPTIONS,
  captions: SPRINT_ISRT_QUADRANT_CAPTIONS, explanations: SPRINT_ISRT_QUADRANT_EXPLANATIONS,
};
